<template lang="html">
  <report-builder
    v-model="reportFilters"
    title="Dining Report"
    :sub-title="subTitle"
    type="dining"
    command="diningReservationDetailReport"
    saved-report-command="diningSavedReport"
    :command-filter="commandFilter"
    :resources="reportFilterResourceCommands"
    :defaults="defaultReportFilters"
    :headers="headers"
    :report-fields="includeFields"
    :report-filters="currentFilterSelection"
    :report-order="sortOrder"
    :report-grouping="groupOrder"
    :report-grouping-fields="getGroupFields"
    :before-run="beforeRun"
    :grouped="isGrouped"
    show-summary
    summary-caption="Summary"
  >
    <!-- REPORT FORM -->
    <!--  DATE  -->
    <template v-slot:filter-date>
      <v-layout>
        <v-flex class="xs6">
          <date-picker
            v-model="reportFilters.date"
            label="Start Date"
          />
        </v-flex>
        <v-flex class="xs6 pl-1">
          <date-picker
            v-model="reportFilters.endDate"
            :icon="null"
            label="End Date"
            clearable
          />
        </v-flex>
      </v-layout>
    </template>

    <!--  MEAL PERIOD FILTER  -->
    <template v-slot:filter-meal-period>
      <v-autocomplete
        v-model="reportFilters.mealPeriodIds"
        prepend-icon="mdi-silverware-fork-knife"
        label="Meal Period"
        :items="mealPeriods.filter(mp => !mp.disabled)"
        multiple
        item-text="name"
        item-value="id"
        chips
        autocomplete
        clearable
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <v-chip
            :key="JSON.stringify(data.item)"
            :input-value="data.selected"
            class="chip--select-multi"
            @input="data.parent.selectItem(data.item)"
            @click="data.parent.selectItem(data.item)"
          >
            {{ data.item.name }}
          </v-chip>
        </template>
        <template v-slot:item="{ active, item, attrs, on }">
          <v-list-item
            v-bind="attrs"
            v-on="on"
          >
            <v-list-item-action>
              <v-checkbox :input-value="attrs.inputValue" />
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ item.name }}
              </v-list-item-title>
              <v-list-item-subtitle>{{ item.locationName }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-icon>
              <v-tooltip
                v-if="item.overlappingIds.length"
                left
              >
                <template v-slot:activator="tt">
                  <v-btn
                    icon
                    v-bind="tt.attrs"
                    v-on="tt.on"
                    @click.stop="toggleMealPeriods(item.overlappingIds)"
                  >
                    <v-icon>mdi-vector-link</v-icon>
                  </v-btn>
                </template>
                <span>Overlaps Other Meal Period(s)</span>
              </v-tooltip>
            </v-list-item-icon>
          </v-list-item>
        </template>
      </v-autocomplete>
    </template>

    <!--  LOCATION FILTER -->
    <template v-slot:filter-location>
      <v-autocomplete
        v-model="reportFilters.locationIds"
        prepend-icon="mdi-map-marker"
        label="Location"
        :items="locations.filter(l => !reportFilters.mealPeriodIds[0] || reportFilters.mealPeriodIds.find(mpId => (mealPeriodMap[mpId] || {}).locationId === l.id))"
        multiple
        item-text="name"
        item-value="id"
        chips
        autocomplete
        clearable
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <v-chip
            :key="JSON.stringify(data.item)"
            :input-value="data.selected"
            class="chip--select-multi"
            @input="data.parent.selectItem(data.item)"
            @click="data.parent.selectItem(data.item)"
          >
            {{ data.item.name }}
          </v-chip>
        </template>
      </v-autocomplete>
    </template>

    <!--  REPORT COLUMNS  -->
    <template v-slot:filter-columns-lg>
      <v-autocomplete
        v-model="reportFilters.columns"
        prepend-icon="mdi-view-list"
        label="Columns"
        :items="columnOptions.filter(c => c.optional)"
        multiple
        item-text="name"
        item-value="id"
        chips
        autocomplete
        clearable
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <v-chip
            :key="JSON.stringify(data.item)"
            :input-value="data.selected"
            class="chip--select-multi"
            @input="data.parent.selectItem(data.item)"
            @click="data.parent.selectItem(data.item)"
          >
            {{ data.item.name }}
          </v-chip>
        </template>
      </v-autocomplete>
    </template>

    <!--  REPORT DETAIL DISPLAY  -->
    <template v-slot:filter-detail_grouping>
      <v-autocomplete
        v-model="reportFilters.detailGrouping"
        prepend-icon="mdi-account-group"
        label="Reservation Display"
        :items="detailGroupingOptions"
        item-text="name"
        item-value="id"
      />
    </template>

    <!--  REPORT GROUPING -->
    <template v-slot:filter-grouping>
      <v-autocomplete
        v-model="reportFilters.grouping"
        prepend-icon="mdi-sort"
        label="Grouping"
        :items="groupingOptions"
        item-text="name"
        item-value="id"
        clearable
      />
    </template>

    <!--  REPORT SORT ORDER -->
    <template v-slot:filter-order>
      <v-autocomplete
        v-model="reportFilters.sortBy"
        prepend-icon="mdi-sort"
        label="Sort Order"
        :items="sortOptions"
        item-text="name"
        item-value="id"
      />
    </template>

    <!--  REPORT BLANK LINES AFTER GROUP -->
    <template v-slot:filter-group_lines>
      <v-autocomplete
        v-if="reportFilters.grouping"
        v-model="reportFilters.emptyRows"
        prepend-icon="mdi-table-row-plus-after"
        label="Add empty rows per group"
        :items="emptyRowOptions"
      />
    </template>

    <!-- INCLUDE WAITLISTED -->
    <template v-slot:filter-waitlisted>
      <v-autocomplete
        v-model="reportFilters.includeWaitListMode"
        prepend-icon="mdi-pause-octagon-outline"
        label="Include Waitlisted"
        :items="waitlistModes"
      />
    </template>

    <!-- REPORT HEADERS -->
    <!-- (No header slot overrides needed) -->

    <!-- REPORT ROWS/COLUMNS -->
    <template
      slot="td-names"
      slot-scope="props"
    >
      <span
        v-for="(name, i) in props.header.displayCell(props.row, props.index, props.rows)"
        :key="i"
        class="ft-report-reservee"
      >
        <span
          v-if="name.guestType && !isNonDiscreteRoot(props.row)"
          :class="{'ft-report-reservee-gt': true, 'ft-has-name': !!name.name}"
        >{{ name.guestType }}</span>
        <span
          v-if="name.name"
          class="ft-report-reservee-name"
        >{{ name.name }}</span>
        <span
          v-if="name.count > 1"
          class="ft-report-reservee-count"
        >{{ name.count }}</span>
      </span>
    </template>
    <template
      slot="aggregate-explodeAnswers"
      slot-scope="props"
    >
      <div>
        <span
          v-for="(answerAggregate, i) in props.aggregate"
          :key="i"
          class="ft-itb"
        >
          <span class="ft-itb-text">{{ answerAggregate.value }}</span><span class="ft-itb-sep">:&nbsp;</span><span class="ft-itb-value">{{ answerAggregate.total }}</span>
          <span
            v-if="(i + 1) < props.aggregate.length"
            class="ft-itb-sep"
          >,&nbsp;</span>
        </span>
      </div>
    </template>
    <template
      slot="aggregate-explodeAggregate"
      slot-scope="props"
    >
      <div>
        <span
          v-for="(costAggregate, i) in props.aggregate"
          :key="i"
          class="ft-itb"
        >
          <span class="ft-itb-text">{{ costAggregate.name }}</span><span class="ft-itb-sep">:&nbsp;</span><span class="ft-itb-value">{{ costAggregate.total }}</span>
          <span
            v-if="(i + 1) < props.aggregate.length"
            class="ft-itb-sep"
          >,&nbsp;</span>
        </span>
      </div>
    </template>

    <!-- REPORT SUMMARY -->
    <template slot="summary-body">
      <div>Total Reservees: {{ reserveeCount }}</div>
      <div v-if="noShowCount">
        No Shows: {{ noShowCount }}
      </div>
      <div v-if="waitlistedReservees">
        Waitlisted Reservees: {{ waitlistedReservees }}
      </div>
      <div>Total Reservations: {{ reservationCount }}</div>
      <div v-if="waitlistedReservations">
        Waitlisted Reservations: {{ waitlistedReservations }}
      </div>
    </template>
  </report-builder>
</template>

<script>
import {helpers, rules, groupableReport} from '@/ft-vue-core'
import detailCommon from '../common/mixins/detailCommon'
import options from './mixins/options'

const {deepClone, verboseDate} = helpers

const INCLUDE_WAITLISTED = 0
const EXCLUDE_WAITLISTED = 1
const ONLY_WAITLISTED = 2

const defaultReportFilters = {
  scale: 1,
  mealPeriodIds: [],
  locationIds: [],
  sortBy: 1,
  grouping: null,
  detailGrouping: 2,
  columns: [1, 2, 3, 29],
  date: null,
  endDate: null,
  paperSize: null, // Defaults to LETTER
  orientation: null, // Defaults to PORTRAIT
  email: null,
  emptyRows: 0,
  includeWaitListMode: INCLUDE_WAITLISTED
}

export default {
  name: 'DiningReport',
  mixins: [rules, groupableReport, detailCommon, options],
  data: function () {
    return {
      defaultReportFilters: deepClone(defaultReportFilters),
      reportFilters: deepClone(defaultReportFilters),
      mealPeriods: [],
      waitlistModes: [
        {text: 'Yes', value: INCLUDE_WAITLISTED},
        {text: 'No', value: EXCLUDE_WAITLISTED},
        {text: 'Only', value: ONLY_WAITLISTED}
      ]
    }
  },
  computed: {
    subTitle () {
      return this.selectedDetailGrouping.subTitle + (this.reportFilters.date ? ' - ' + verboseDate(this.reportFilters.date) : '')
    },
    mealPeriodMap () {
      return this.mealPeriods.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    detailGroupingMap () {
      return this.detailGroupingOptions.reduce((result, item) => {
        result[item.id] = item
        return result
      }, {})
    },
    selectedDetailGrouping () {
      return this.detailGroupingMap[this.reportFilters.detailGrouping] || {}
    },
    /* overrides super */
    selectedGroupingFilters () {
      return this.selectedGrouping.concat(this.selectedDetailGrouping.group || [])
    },
    groupedByReservation () {
      return this.selectedDetailGrouping.group && this.selectedDetailGrouping.group[0] || this.selectedDetailGrouping.consolidated === true
    },
    displayConsolidated () {
      return this.selectedDetailGrouping.consolidated === true
    }
  },
  /**
   * TODO: Move any methods that don't require access to "this" to a helpers include
   *       There is overhead in calling this.xxx methods
   */
  methods: {
    beforeRun (f) {
      this.subGroupHighlight = true
    },
    getExtraIncludeFields () {
      // Add any fields needed by the selected detail grouping option
      return this.getGroupFields(this.selectedDetailGrouping)
    },
    reservationChanged (row, rowIndex, rows) {
      return !this.groupedByReservation || (rowIndex === 0 || row.reservationId !== rows[rowIndex - 1].reservationId)
    },
    skipRow (row, rowIndex) {
      // Skip any non-discreteCovers.  (probably should move this to the query filter)
      return this.selectedDetailGrouping.consolidated === true && row.pos > 0 || !row.discreteCovers && row.pos > 0 && !row.memberId
    },
    extraSortOrder (f) {
      // Groupable mixin sorts by grouped order first, then we add any other sorting
      return (this.sortOptionMap[f.sortBy] || {}).order || []
    },
    currentFilterSelection (f) {
      var filters = []
      var reservationFilters = []
      // DATES
      if (f.date && f.endDate) {
        reservationFilters.push({
          field: 'dateTime',
          operator: 'BETWEEN',
          values: [
            f.date + ' 00:00:00',
            f.endDate + ' 23:59:59'
          ]
        })
      } else if (f.date) {
        reservationFilters.push({
          field: 'dateTime',
          operator: 'BETWEEN',
          values: [
            f.date + ' 00:00:00',
            f.date + ' 23:59:59'
          ]
        })
      }
      // LOCATIONS
      if (f.locationIds && f.locationIds.length) {
        reservationFilters.push({field: 'locationId', operator: 'IN', values: f.locationIds})
      }
      // MEAL PERIODS
      if (f.mealPeriodIds && f.mealPeriodIds.length) {
        // Get specific meal periods
        reservationFilters.push({field: 'mealPeriodId', operator: 'IN', values: f.mealPeriodIds})
      } else {
        // Get non-event reservations
        // We don't check for NON NULL mealPeriodId in reports. If these are older reservations, they may have been orphaned.
        // All we have is the mealPeriodName as it was when originally reserved.
        // If you want to exclude current orphaned reservations, check if they are in the issues list
        reservationFilters.push({field: 'mealPeriodName', operator: 'IS_NOT_NULL'})
      }
      // RESERVATION SUB FILTER
      if (reservationFilters.length) {
        filters.push({
          field: 'reservationId',
          operator: 'IN',
          subQueryTarget: 'diningReservation.id',
          subQueryFilter: reservationFilters
        })
      }
      // WAITLIST MODE
      if (f.includeWaitListMode > 0) {
        filters.push({field: 'waitlist', operator: 'EQUAL', values: [f.includeWaitListMode === ONLY_WAITLISTED]})
      }
      // EXCLUDE CANCELLED
      filters.push({field: 'cancelled', operator: 'IS_NULL'})
      console.log('Selected Filters', filters)
      return filters
    },
    validFilters () {
      return true
    },
    reportFilterResourceCommands () {
      return [
        {command: 'diningLocation', load: this.locations},
        {command: 'diningMealPeriod', load: this.mealPeriods, params: {include_disabled: false, include_fields: ['overlappingIds', 'locationName']}}
      ]
    },
    toggleMealPeriods (mpIds) {
      const f = this.reportFilters
      const mpSelected = f.mealPeriodIds
      const mpMap = mpIds.reduce((a, m) => {a[m] = true; return a}, {})
      const mpsMap = mpSelected.reduce((a, m) => {a[m] = true; return a}, {})
      const notAll = mpIds.find(m => !mpsMap[m])
      if (notAll) {
        f.mealPeriodIds = mpSelected.filter(m => !mpMap[m]).concat(mpIds)
      } else {
        f.mealPeriodIds = mpSelected.filter(m => !mpMap[m])
      }
    },
  }
}
</script>

<style>
  td .ft-report-reservee {
    white-space: nowrap;
    display: inline-block;
  }
  td .ft-report-reservee .ft-report-reservee-gt.ft-has-name::after {
    content: ": ";
  }
  td .ft-report-reservee .ft-report-reservee-count::before {
    content: " (";
  }
  td .ft-report-reservee .ft-report-reservee-count::after {
    content: ")";
  }
  td .ft-report-reservee:nth-last-child(n+2)::after {
    content: "; ";
  }
</style>
